import React, { useState, useEffect } from 'react';
import { CheckCircle2, Shield } from 'lucide-react';
import { Player } from '../../types';
import { formatPlayerName } from '../../lib/utils';
import { FadeInImage } from './FadeInImage';

export interface ScorePickerPair {
  ids: string[];
  label: string;   // fallback label
  color?: string;  // tailwind text color class
}

interface Props {
  pair1: ScorePickerPair;
  pair2: ScorePickerPair;
  players: Player[];
  onSave: (s1: string, s2: string, s3: string) => Promise<void>;
  onCancel?: () => void;
}

type SetId = 1 | 2 | 3;
type TeamId = 1 | 2;

const isValidPadelSet = (str: string): boolean => {
  if (!str) return false;
  const [a, b] = str.split('-').map(Number);
  if (isNaN(a) || isNaN(b)) return false;
  if (a === 6 && b <= 4) return true;
  if (b === 6 && a <= 4) return true;
  if (a === 7 && (b === 5 || b === 6)) return true;
  if (b === 7 && (a === 5 || a === 6)) return true;
  return false;
};

const getScoreVal = (setRaw: string | undefined, team: TeamId): string => {
  if (!setRaw) return '';
  const parts = setRaw.split('-');
  return team === 1 ? parts[0] || '' : parts[1] || '';
};

const getWinnerOfSet = (setRaw: string): TeamId | null => {
  if (!setRaw) return null;
  const [a, b] = setRaw.split('-').map(Number);
  if (isNaN(a) || isNaN(b)) return null;
  if (a > b) return 1;
  if (b > a) return 2;
  return null;
};

// Mini inline avatar stack for 2 players side by side (overlapping)
function MiniPairAvatar({ ids, players }: { ids: string[]; players: Player[] }) {
  const p1 = players.find(x => x.id === ids[0]);
  const p2 = players.find(x => x.id === ids[1]);

  const Avatar = ({ photo, name }: { photo?: string; name?: string }) => (
    <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-black/60 bg-white/10 flex items-center justify-center shrink-0">
      {photo ? (
        <FadeInImage src={photo} alt={name} className="w-full h-full object-cover" />
      ) : (
        <span className="text-xs font-black text-white/40">
          {name ? name.charAt(0).toUpperCase() : <Shield size={12} className="opacity-30" />}
        </span>
      )}
    </div>
  );

  return (
    <div className="flex shrink-0">
      <Avatar photo={p1?.photoUrl} name={p1?.name} />
      {/* second avatar overlaps slightly */}
      <div className="-ml-3">
        <Avatar photo={p2?.photoUrl} name={p2?.name} />
      </div>
    </div>
  );
}

export function ScorePicker({ pair1, pair2, players, onSave, onCancel }: Props) {
  const [s1, setS1] = useState('');
  const [s2, setS2] = useState('');
  const [s3, setS3] = useState('');
  const [activeBox, setActiveBox] = useState<{ set: SetId; team: TeamId } | null>({ set: 1, team: 1 });
  const [localError, setLocalError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const s1Winner = getWinnerOfSet(s1);
  const s2Winner = getWinnerOfSet(s2);
  const showSet3 = !!(s1Winner && s2Winner && s1Winner !== s2Winner);

  useEffect(() => {
    if (s1Winner && s2Winner && s1Winner === s2Winner && s3 !== '') {
      setS3('');
    }
  }, [s1Winner, s2Winner]);

  // Build pair label names: "Apellido1 Apellido2"
  const pairLabel = (ids: string[]) =>
    ids.map(id => {
      if (id.startsWith('guest_')) return 'Invitado';
      const p = players.find(pl => pl.id === id);
      return p ? formatPlayerName(p.name, 'minimal') : '?';
    }).join(' ');

  const handleNumberSelect = (num: number) => {
    if (!activeBox) return;
    const { set, team } = activeBox;

    const currentScoreStr = set === 1 ? s1 : set === 2 ? s2 : s3;
    const parts = currentScoreStr.split('-');
    const newT1 = team === 1 ? num.toString() : (parts[0] || '');
    const newT2 = team === 2 ? num.toString() : (parts[1] || '');
    const newScore = `${newT1}-${newT2}`;

    if (set === 1) setS1(newScore);
    else if (set === 2) setS2(newScore);
    else setS3(newScore);

    const otherTeam: TeamId = team === 1 ? 2 : 1;
    const otherVal = getScoreVal(currentScoreStr, otherTeam);

    if (otherVal === '') {
      setActiveBox({ set, team: otherTeam });
    } else {
      const t1Val = team === 1 ? num : parseInt(getScoreVal(currentScoreStr, 1) || '0');
      const t2Val = team === 2 ? num : parseInt(getScoreVal(currentScoreStr, 2) || '0');
      const setStr = `${t1Val}-${t2Val}`;
      if (!isValidPadelSet(setStr)) {
        setActiveBox({ set, team: otherTeam });
        return;
      }
      if (set === 1) {
        setActiveBox({ set: 2, team: 1 });
      } else if (set === 2) {
        const s1W = getWinnerOfSet(s1);
        const s2W = t1Val > t2Val ? 1 : t2Val > t1Val ? 2 : null;
        if (s1W && s2W && s1W !== s2W) {
          setActiveBox({ set: 3, team: 1 });
        } else {
          setActiveBox(null);
        }
      } else {
        setActiveBox(null);
      }
    }
  };

  const handleConfirm = async () => {
    if (!isValidPadelSet(s1)) {
      setLocalError('⚠️ El Set 1 tiene un resultado inválido para Padel.');
      setTimeout(() => setLocalError(''), 4000);
      setActiveBox({ set: 1, team: 1 });
      return;
    }
    if (!isValidPadelSet(s2)) {
      setLocalError('⚠️ El Set 2 tiene un resultado inválido para Padel.');
      setTimeout(() => setLocalError(''), 4000);
      setActiveBox({ set: 2, team: 1 });
      return;
    }
    if (showSet3 && (!s3 || !isValidPadelSet(s3))) {
      setLocalError('⚠️ Debes completar el Set 3 (están 1 a 1).');
      setTimeout(() => setLocalError(''), 4000);
      setActiveBox({ set: 3, team: 1 });
      return;
    }

    setIsSaving(true);
    setTimeout(async () => {
      try {
        await onSave(s1, s2, s3);
        setIsSuccess(true);
      } catch (e) {
        console.error('Error guardando', e);
      } finally {
        setIsSaving(false);
      }
    }, 600);
  };

  const renderBox = (setNum: SetId, teamNum: TeamId) => {
    const isVisible = setNum !== 3 || showSet3 || s3 !== '';
    if (!isVisible) {
      return (
        <div className="w-9 h-9 border border-dashed border-white/10 rounded-xl opacity-20 bg-white/5 pointer-events-none" />
      );
    }

    const isActive = activeBox?.set === setNum && activeBox?.team === teamNum;
    const val = getScoreVal(setNum === 1 ? s1 : setNum === 2 ? s2 : s3, teamNum);

    return (
      <div
        onClick={() => {
          if (activeBox?.set === setNum && activeBox?.team === teamNum) {
            setActiveBox(null);
          } else {
            setActiveBox({ set: setNum, team: teamNum });
          }
        }}
        className={`w-9 h-9 rounded-xl font-black text-center flex items-center justify-center transition-all text-sm cursor-pointer select-none relative z-[150] ${
          isActive
            ? 'border-2 theme-primary-border bg-theme-primary/20 shadow-lg scale-110'
            : 'border border-white/10 bg-black/50 hover:border-white/30'
        }`}
      >
        <span className={val !== '' ? 'text-white' : 'text-white/20'}>
          {val !== '' ? val : '–'}
        </span>
      </div>
    );
  };

  return (
    <div className="w-full space-y-2">
      {/* Score grid container */}
      <div className="bg-black/20 border border-white/5 rounded-2xl overflow-hidden">

        {/* Column headers */}
        <div className="flex items-center px-4 pt-3 pb-1">
          <div className="flex-1 min-w-0" />
          <div className="flex gap-2 shrink-0 mr-[66px]">
            <span className="w-9 text-center text-[7px] font-black text-white/25 uppercase tracking-widest">S1</span>
            <span className="w-9 text-center text-[7px] font-black text-white/25 uppercase tracking-widest">S2</span>
            <span className="w-9 text-center text-[7px] font-black uppercase tracking-widest transition-all" style={{ color: showSet3 ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.08)' }}>S3</span>
          </div>
        </div>

        {/* Rows */}
        <div className="relative px-3 pb-3 flex flex-col gap-2 pr-[70px]">

          {/* Team 1 Row */}
          <div className="flex items-center gap-2 sm:gap-3">
            <MiniPairAvatar ids={pair1.ids} players={players} />
            <span className="flex-1 min-w-0 text-[8.5px] sm:text-[10px] font-bold uppercase leading-tight text-white/80 line-clamp-2 break-words">
              {pairLabel(pair1.ids) || pair1.label}
            </span>
            <div className="flex gap-2 shrink-0">
              {renderBox(1, 1)}
              {renderBox(2, 1)}
              {renderBox(3, 1)}
            </div>
          </div>

          {/* Team 2 Row */}
          <div className="flex items-center gap-2 sm:gap-3">
            <MiniPairAvatar ids={pair2.ids} players={players} />
            <span className="flex-1 min-w-0 text-[8.5px] sm:text-[10px] font-bold uppercase leading-tight text-white/80 line-clamp-2 break-words">
              {pairLabel(pair2.ids) || pair2.label}
            </span>
            <div className="flex gap-2 shrink-0">
              {renderBox(1, 2)}
              {renderBox(2, 2)}
              {renderBox(3, 2)}
            </div>
          </div>

          {/* Save Button — absolute right side, vertically centered */}
          <button
            onClick={handleConfirm}
            disabled={isSaving || isSuccess || activeBox !== null}
            className={`absolute right-3 top-1/2 -translate-y-1/2 h-[52px] w-[50px] rounded-xl font-black flex flex-col items-center justify-center gap-1 text-[7px] sm:text-[8px] uppercase tracking-wider transition-all border active:scale-95 shadow-lg z-10 ${
              isSuccess
                ? 'theme-primary-bg text-black border-transparent'
                : activeBox !== null
                  ? 'bg-white/5 text-white/10 border-white/5 cursor-not-allowed'
                  : 'theme-primary-bg text-black border-transparent hover:opacity-90'
            }`}
          >
            {isSaving ? (
              <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
            ) : (
              <>
                <CheckCircle2 size={15} />
                <span>{isSuccess ? 'Listo' : 'Guardar'}</span>
              </>
            )}
          </button>
        </div>

        {/* Error message */}
        {localError && (
          <div className="px-4 py-2 bg-red-500 text-white text-[9px] font-black uppercase text-center animate-in slide-in-from-top-1 fade-in">
            {localError}
          </div>
        )}

        {/* Number Picker */}
        {activeBox && (
          <>
            <div
              className="fixed inset-0 z-[140] cursor-default"
              onClick={() => setActiveBox(null)}
            />
            <div className="relative z-[150] border-t border-white/10 bg-[#1a1a28]/98 backdrop-blur-md px-3 py-3 animate-in slide-in-from-top-2 fade-in duration-150">
              <div className="flex justify-center gap-1.5">
                {[0, 1, 2, 3, 4, 5, 6, 7].map(n => (
                  <button
                    key={n}
                    onClick={() => handleNumberSelect(n)}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#eab308';
                      (e.currentTarget as HTMLButtonElement).style.color = '#000';
                      (e.currentTarget as HTMLButtonElement).style.borderColor = '#eab308';
                      (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 0 16px rgba(234,179,8,0.7)';
                      (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.15)';
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLButtonElement).style.backgroundColor = '';
                      (e.currentTarget as HTMLButtonElement).style.color = '';
                      (e.currentTarget as HTMLButtonElement).style.borderColor = '';
                      (e.currentTarget as HTMLButtonElement).style.boxShadow = '';
                      (e.currentTarget as HTMLButtonElement).style.transform = '';
                    }}
                    className="flex-1 max-w-[36px] h-10 bg-white/10 rounded-xl font-black text-sm transition-all duration-150 active:scale-90 border border-white/20 text-white"
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Cancel link */}
      {onCancel && (
        <button
          onClick={onCancel}
          className="w-full text-[9px] text-white/25 uppercase tracking-widest hover:text-white/50 transition-colors pt-1"
        >
          Cancelar
        </button>
      )}
    </div>
  );
}
